import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase, type VerimatrixDRMConfig } from './configs';
import type { Verimatrix } from './shared-verimatrix-types';

// ============================================
// Query keys
// ============================================

export const sharedVerimatrixKeys = {
  list: (prefix: string, serviceId: string) =>
    [prefix, 'verimatrixes', { serviceId }] as const,
  detail: (prefix: string, id: string | number, serviceId: string) =>
    [prefix, 'verimatrix_detail', { id, serviceId }] as const,
};

// ============================================
// Queries
// ============================================

export function useVerimatrixes(
  serviceId: string,
  config: VerimatrixDRMConfig,
) {
  return useQuery<Verimatrix[]>({
    queryKey: sharedVerimatrixKeys.list(config.queryKeyPrefix, serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, config.kind)}/verimatrixes`);
      return res.data?.data ?? res.data ?? [];
    },
  });
}

export function useVerimatrixDetail(
  serviceId: string,
  config: VerimatrixDRMConfig,
  id: string | number | null,
) {
  return useQuery<Verimatrix>({
    queryKey: sharedVerimatrixKeys.detail(config.queryKeyPrefix, id ?? '', serviceId),
    enabled: !!serviceId && !!id,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, config.kind)}/verimatrixes/${id}`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateVerimatrix(
  serviceId: string,
  config: VerimatrixDRMConfig,
) {
  const qc = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<Verimatrix, Error, any>({
    mutationFn: async (payload) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, config.kind)}/verimatrixes`, payload);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: sharedVerimatrixKeys.list(config.queryKeyPrefix, serviceId),
      });
    },
  });
}

export function useUpdateVerimatrix(
  serviceId: string,
  config: VerimatrixDRMConfig,
) {
  const qc = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<Verimatrix, Error, { id: string | number; payload: any }>({
    mutationFn: async ({ id, payload }) => {
      const res = await serviceApiV3.put(`${mediaBase(serviceId, config.kind)}/verimatrixes/${id}`, payload);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: sharedVerimatrixKeys.list(config.queryKeyPrefix, serviceId),
      });
    },
  });
}

export function useDeleteVerimatrix(serviceId: string, config: VerimatrixDRMConfig) {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string | number }>({
    mutationFn: async ({ id }) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, config.kind)}/verimatrixes/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: sharedVerimatrixKeys.list(config.queryKeyPrefix, serviceId),
      });
    },
  });
}
